import React from "react";
import { PlusIcon } from "@heroicons/react/24/outline";

// 05 Feb 2026
import {
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";

import { useGetUserCreationsQuery } from "@/features/userCreations/userCreationsApiSlice";

import { useGetOrganizationsQuery } from "@/features/organizations/organizationsApiSlice"; // 30 Jan 2026
import { useGetUsersQuery } from "@/features/users/usersApiSlice"; // 30 Jan 2026

import { useGetCreationTypesQuery } from "@/features/creationTypes/creationTypesApiSlice";
import { useGetCreationAreaTypesQuery } from "@/features/creationAreaTypes/creationAreaTypesApiSlice";
import EtCreationSummaryTiles from "@/components/creations/EtCreationSummaryTiles";


import EtCreationCard from "@/components/creations/EtCreationCard";
import EtModalCreateUserImprintCreation from "@/components/common/modal/EtModalCreateUserImprintCreation";
import EtModalUpdateUserImprintCreation from "@/components/common/modal/EtModalUpdateUserImprintCreation";

import EtWabCreationIcon1SVGComp from "@/assets/img/icons/svgComp/EtWabCreationIcon1SVGComp";

import {
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import moment from "moment";

// 25 Jan 2026
import { CircularProgressbarWithChildren } from 'react-circular-progressbar'

import User from "@/components/common/user/User"

import { userProfileIcon2 } from "@/assets/img/imgAssets"

// 29 Jan 2026
import useAuth from "@/hooks/useAuth";


const EtTrinityCreationCenter = ({ userId = "", userOrgId = "", onClose }) => {
  // First Created - 09 Dec 2025
  // Author - Atul Pandey
  // Purpose - Let user manage all their creations from one place (Log + Chart [+ Summary])
  // Updated - 09 Dec 2025 - Creation Habit Charts (createdAt vs time-of-day)
  // Updated - 09 Dec 2025 - Converted from ReflectionsCenter to use UserCreations
  // Updated - 09 Dec 2025 - Added second chart - day count + cumulative
  // Updated - 02 Jan 2026 - added time chips for "All" and "By Year"
  // Updated - 02 Jan 2026 - refined the end of quarter moment definition
  // Updated - 25 Jan 2026 - refined for org sponsor level data show (Self | Collective)
  // Updated - 25 Jan 2026 - added contributions view
  // Updated - 26 Jan 2026 - refinement of handling collective user view switch from everyone to other users
  // Updated - 26 Jan 2026 - refined for purpose native universe view
  // Updated - 29 Jan 2026 - refined for EtAdmins to dig deeper by org in purpose native universe view
  // Updated - 30 Jan 2026 - continued refinements for EtAdmins to dig deeper by org in purpose native universe view
  // Updated - 30 Jan 2026 - get org data for org matching for etadmin view
  // Updated - 31 Jan 2026 - charts for byOrg
  // Updated - 05 Feb 2026 - org name masking in byOrg
  // Updated - 21 Apr 2026 - Refined My Org view to also show charts


  // 0 - 29 Jan 2026 - Determine user Auth state
  
  const { isEtAdmin, isEtCoach } = useAuth();
  const canViewAdminUniverse = isEtAdmin || isEtCoach;

  // -----------------------------------------
  // 1. Local State
  // -----------------------------------------
  const [activeTimeBucket, setActiveTimeBucket] = React.useState("today");

  // ⭐ 02 Jan 2026 - NEW — active year for "By Year"
  const [activeYear, setActiveYear] = React.useState(moment().year());

  const [searchText, setSearchText] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const [showDetail, setShowDetail] = React.useState({});

  // Modals
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [showUpdateModal, setShowUpdateModal] = React.useState(false);
  const [selectedCreation, setSelectedCreation] = React.useState(null);

  // Views: log, chart, (summary later)
  const [activeViewTab, setActiveViewTab] = React.useState("log");
  const [chartTab, setChartTab] = React.useState("streak");

  // ⭐ 25 Jan 2026 - Phase 3 – View Mode (Self | Collective)
  const [viewMode, setViewMode] = React.useState("self"); // "self" | "collective"
  const [activeCollectiveUserId, setActiveCollectiveUserId] = React.useState("all");

  // ⭐ 25 Jan 2026 - Chart Mode for Creations (Aggregate | Compare)
  const [chartViewMode, setChartViewMode] = React.useState("aggregate"); // "aggregate" | "compare"

  // ⭐ 25 Jan 2026 - Creations Collective Performance Tabs 
  const [collectivePerformanceTab, setCollectivePerformanceTab] = React.useState("charts"); // "charts" | "contributions"

  // ⭐ 29 Jan 2026 — Universe Sub Tabs (Admin only)
  const [universeTab, setUniverseTab] = React.useState("myOrg"); // myOrg | byOrg

  // ⭐ 31 Jan 2026 – selected user per org for chart focus
  const [selectedOrgUserMap, setSelectedOrgUserMap] = React.useState({});

  // ⭐ 05 Feb 2026 — Org name visibility (controls users too)
  const [showOrgNameMap, setShowOrgNameMap] = React.useState({});



  // -----------------------------------------
  // 2. Load Creation Data (Atomic Records)
  // -----------------------------------------
  
  // ⭐ 26 Jan 2026 - Purpose Native Universe View
  const {
    data: creationsResponse,
    isLoading,
    refetch: refetchUserCreations,
  } = useGetUserCreationsQuery();

  // console.log('creationsResponse', creationsResponse)
  
  // ⭐ 26 Jan 2026 — All Creations Raw
  const allCreationsRaw = React.useMemo(() => {
      if (!creationsResponse) return [];
      if (Array.isArray(creationsResponse)) return creationsResponse;
      return creationsResponse.creations || [];
  }, [creationsResponse]);

  // ⭐ 26 Jan 2026 — Org Daily Totals Raw
  const orgDailyTotalsRaw = React.useMemo(() => {
        if (!creationsResponse) return [];
        if (Array.isArray(creationsResponse)) return [];
        return creationsResponse.orgDailyTotals || [];
  }, [creationsResponse]);
  
  // ⭐ 26 Jan 2026 — Normalize Org Daily Totals by Date (Universe Baseline)
  const orgDailyTotalsByDate = React.useMemo(() => {
      if (!orgDailyTotalsRaw || orgDailyTotalsRaw.length === 0) return {};
  
      return orgDailyTotalsRaw.reduce((acc, item) => {
        // Defensive guards (backend already mostly clean, but be safe)
        if (!item?.date) return acc;
        if (typeof item.dailyCount !== "number") return acc;
  
        acc[item.date] = item.dailyCount;
        return acc;
      }, {});
  }, [orgDailyTotalsRaw]);
 
  
  // const {
  //   data: userCreationsData,
  //   isLoading,
  //   refetch: refetchUserCreations,
  // } = useGetUserCreationsQuery();

  // // 25 Jan 2026 - added based on the new fetch logic in the back end which already filters records for user and role
  // const allCreationsRaw = React.useMemo(() => {
  //   if (!userCreationsData?.ids || !userCreationsData?.entities) return [];
  //   return userCreationsData.ids.map((id) => userCreationsData.entities[id]);
  // }, [userCreationsData]);


  // 25 Jan 2026 - prior code - commented out - do not delete - for reference 
  // const allCreationsRaw = React.useMemo(() => {
  //   if (!userCreationsData?.ids || !userCreationsData?.entities) return [];
  //   return userCreationsData.ids
  //     .map((id) => userCreationsData.entities[id])
  //     .filter(
  //       (item) =>
  //         item?.userId?._id === userId && item?.userOrgId?._id === userOrgId
  //     );
  // }, [userCreationsData, userId, userOrgId]);

  // -----------------------------------------
  // 2B. Load Creation Types & Area Types (for Summary)
  // -----------------------------------------
  const { data: creationTypesData } = useGetCreationTypesQuery();
  const { data: creationAreaTypesData } = useGetCreationAreaTypesQuery();

  // 30 Jan 2026 - Extract Organizations 
  const { data: organizationsData = [] } = useGetOrganizationsQuery();
  const { data: usersResponse } = useGetUsersQuery(undefined, {
    skip: !canViewAdminUniverse,
  });

  const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id]);
  };

  const organizations = extractEntitiesFromAdapter(organizationsData);
  const allUsers = extractEntitiesFromAdapter(usersResponse);

  // console.log('users', allUsers)

  const creationTypes = React.useMemo(
    () => extractEntitiesFromAdapter(creationTypesData),
    [creationTypesData]
  );

  const creationAreaTypes = React.useMemo(
    () => extractEntitiesFromAdapter(creationAreaTypesData),
    [creationAreaTypesData]
  );

  // 30 Jan 2026 - Build OrgId to organizationName map to be used in byOrg tab in Universe view
  const orgIdToNameMap = React.useMemo(() => {
    const map = {};
    organizations.forEach(org => {
      map[org._id?.toString()] = org.organizationName || "Unknown Org";
    });
    return map;
  }, [organizations]);

  // console.log('orgIdToNameMap', orgIdToNameMap)


  // -----------------------------------------
  // 3. Preprocess Creation (strip HTML once)
  // -----------------------------------------
  const preprocessHtml = (html = "") =>
    html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

  const allCreations = React.useMemo(() => {
    return allCreationsRaw.map((creation) => {
      const combinedText = `${creation.userCreationName || ""} ${
        creation.userCreationDesc || ""
      }`;
      return {
        ...creation,
        __plainText: preprocessHtml(combinedText),
      };
    });
  }, [allCreationsRaw]);

  // ⭐ 25 Jan 2026 -  Unique users for Collective view
  const collectiveUsers = React.useMemo(() => {
    // if (viewMode !== "collective") return [];

    const map = new Map();

    allCreations.forEach(ref => {
      if (ref.userId?._id && !map.has(ref.userId._id)) {
        map.set(ref.userId._id, {
          _id: ref.userId._id,
          name: ref.userId.name || "User",
          username: ref.userId.username || "",
        });
      }
    });

    return Array.from(map.values());
  }, [
    allCreations, 
    // viewMode
  ]);

  // console.log('collective users : ', collectiveUsers)
  
  // 25 Jan 2026 - Reset the users based on the viewMode
  React.useEffect(() => {
    if (viewMode === "collective") {
      setActiveCollectiveUserId("all");
    }
  }, [viewMode]);


  // 29 Jan 2026 - Universe Subtab reset
  React.useEffect(() => {
    if (viewMode !== "universe") return;
    setUniverseTab("myOrg");
  }, [viewMode]);


  // -----------------------------------------
  // ⭐ 25 Jan 2026 - Phase 3 – User Scoped Creations (Self | Collective)
  // -----------------------------------------
  const userScopedCreations = React.useMemo(() => {
    // SELF → logged-in user only
    if (viewMode === "self") {
      return allCreations.filter(
        c => c.userId?._id === userId
      );
    }

    // COLLECTIVE → Everyone
    if (activeCollectiveUserId === "all") {
      return allCreations;
    }

    // COLLECTIVE → Specific user
    return allCreations.filter(
      c => c.userId?._id === activeCollectiveUserId
    );
  }, [
    allCreations,
    viewMode,
    activeCollectiveUserId,
    userId,
  ]);

  // ⭐ 25 Jan 2026 - Fix: prevent stale "compare" mode when collective user != Everyone
  React.useEffect(() => {
    if (viewMode !== "collective") {
      setChartViewMode("aggregate");
      return;
    }

    if (activeCollectiveUserId !== "all") {
      setChartViewMode("aggregate");
    }
  }, [viewMode, activeCollectiveUserId]);


  // -----------------------------------------
  // 4. Debounce Search (critical for performance)
  // -----------------------------------------
  React.useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchText]);

  // -----------------------------------------
  // 5. Time Boundaries
  // -----------------------------------------
  const now = moment();
  const startOfToday = moment().startOf("day");
  const endOfToday = moment().endOf("day");

  const startOfWeek = moment().isoWeekday(1).startOf("day");
  const endOfWeek = endOfToday;

  const actualEndOfWeek = moment().isoWeekday(7).endOf("day"); // 02 Jan 2026 Sunday of current week

  const startOfMonth = moment().startOf("month");
  const endOfMonth = moment().isSame(now, "month")
    ? endOfToday
    : moment().endOf("month");

  const startOfQuarter = moment().startOf("quarter");
  const endOfQuarter = moment().isSame(now, "quarter")
    ? endOfToday
    : moment().endOf("quarter");

  const startOfYear = moment().startOf("year");
  const endOfYear = moment().isSame(now, "year")
    ? endOfToday
    : moment().endOf("year");

  // -----------------------------------------
  // 6. Time-Based Filtering (using createdAt)
  // -----------------------------------------

    const filterByTime = (items, start, end) =>
    items.filter((c) => {
      // const d = moment.utc(c.createdAt);  // ⬅ parse in UTC
      const d = moment(c.createdAt);        // 25 Jan 2026
      if (!d.isValid()) return false;

      return d.isBetween(moment.utc(start), moment.utc(end), null, "[]");
    });

    // const filterByTime = (items, start, end) =>
    // items.filter((c) => {
    //   const d = moment(c.createdAt);

    //   // 🔥 FIX: skip invalid dates (prevents 2023/1970/garbage dates)
    //   if (!d.isValid()) return false;

    //   return d.isBetween(start, end, null, "[]");
    // });

  // const filterByTime = (items, start, end) =>
  //   items.filter((c) => {
  //     const d = moment(c.createdAt);
  //     return d.isBetween(start, end, null, "[]");
  //   });

  // 25 Jan 2026 - switched from allCreations to userScopedCreations
  const creationsToday = filterByTime(userScopedCreations, startOfToday, endOfToday);
  const creationsThisWeek = filterByTime(userScopedCreations, startOfWeek, endOfWeek);
  const creationsThisMonth = filterByTime(
    userScopedCreations,
    startOfMonth,
    endOfMonth
  );
  const creationsThisQuarter = filterByTime(
    userScopedCreations,
    startOfQuarter,
    endOfQuarter
  );
  const creationsThisYear = filterByTime(
    userScopedCreations,
    startOfYear,
    endOfYear
  );


  // 25 Jan 2026 - Prior code - commented out - kept for reference
  // const creationsToday = filterByTime(allCreations, startOfToday, endOfToday);
  // const creationsThisWeek = filterByTime(allCreations, startOfWeek, endOfWeek);
  // const creationsThisMonth = filterByTime(
  //   allCreations,
  //   startOfMonth,
  //   endOfMonth
  // );
  // const creationsThisQuarter = filterByTime(
  //   allCreations,
  //   startOfQuarter,
  //   endOfQuarter
  // );
  // const creationsThisYear = filterByTime(
  //   allCreations,
  //   startOfYear,
  //   endOfYear
  // );

  // ⭐ 02 Jan 2026 NEW — derive available years for By Year selector
  const availableYears = React.useMemo(() => {
    const years = new Set();

    allCreations.forEach((c) => {
      const y = moment(c.createdAt).year();
      years.add(y);
    });

    const sorted = Array.from(years).sort((a, b) => b - a);

    // Always include current year (even if no creations yet)
    const currentYear = moment().year();
    if (!sorted.includes(currentYear)) {
      sorted.unshift(currentYear);
    }

    return sorted;
  }, [allCreations]);


  // console.log('allCreations', allCreations)
  // console.log('This Year Creations', creationsThisYear)

  // -----------------------------------------
  // 7. Determine displayed set
  // -----------------------------------------  
  let displayedCreations = [];

  switch (activeTimeBucket) {
    case "week":
      displayedCreations = creationsThisWeek;
      break;
    case "month":
      displayedCreations = creationsThisMonth;
      break;
    case "quarter":
      displayedCreations = creationsThisQuarter;
      break;
    case "year":
      displayedCreations = creationsThisYear;
      break;
    
    // ⭐ 02 Jan 2026 NEW — By Year
    case "byYear":
      // displayedCreations = allCreations.filter((c) =>        // 25 Jan 2026 - commented out
      displayedCreations = userScopedCreations.filter((c) =>           // 25 Jan 2026 - added
        moment(c.createdAt).year() === activeYear
      );
      break;

    case "all":
      // displayedCreations = allCreations;       // 25 Jan 2026 - commented out
      displayedCreations = userScopedCreations;   // 25 Jan 2026 - added
      break;
    case "today":
    default:
      displayedCreations = creationsToday;
      break;
  }

  // -----------------------------------------
  // 8. Fuzzy Search - Token Based (same as reflections)
  // -----------------------------------------
  const tokenizeSearch = (text) =>
    text
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

  function levenshteinDistance(a, b) {
    if (!a || !b) return 999;
    const dp = Array(a.length + 1)
      .fill(null)
      .map(() => Array(b.length + 1).fill(null));

    for (let i = 0; i <= a.length; i++) dp[i][0] = i;
    for (let j = 0; j <= b.length; j++) dp[0][j] = j;

    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }

    return dp[a.length][b.length];
  }

  function matchesCreation(plainText, search) {
    if (!search.trim()) return true;

    const searchTokens = tokenizeSearch(search);
    const tokens = tokenizeSearch(plainText);

    return searchTokens.some((searchToken) =>
      tokens.some((word) => {
        if (word === searchToken) return true;
        if (word.startsWith(searchToken)) return true;
        if (word.includes(searchToken)) return true;

        if (Math.abs(word.length - searchToken.length) <= 2) {
          if (levenshteinDistance(word, searchToken) <= 1) return true;
        }

        return false;
      })
    );
  }

  // -----------------------------------------
  // 9. FINAL FILTERED CREATIONS
  // -----------------------------------------
  const finalCreations = React.useMemo(() => {
    if (!displayedCreations) return [];
    if (debouncedSearch.trim() === "") return displayedCreations;

    return displayedCreations.filter((c) =>
      matchesCreation(c.__plainText || "", debouncedSearch)
    );
  }, [displayedCreations, debouncedSearch]);

  // -----------------------------------------
  // 10. Highlight (simple, safe)
  // -----------------------------------------
  const createMarkup = (html) => {
    if (!debouncedSearch.trim()) return { __html: html };

    const words = debouncedSearch.toLowerCase().split(/\s+/).filter(Boolean);
    let highlighted = html;

    words.forEach((w) => {
      if (!w) return;
      const regex = new RegExp(`(${w})`, "gi");
      highlighted = highlighted.replace(
        regex,
        `<mark style="background-color:#f59e0b; color:black; padding:0 2px;">$1</mark>`
      );
    });

    return { __html: highlighted };
  };

  // -----------------------------------------
  // 11. Handlers
  // -----------------------------------------
  const toggleShowDetail = (id) =>
    setShowDetail((prev) => ({ ...prev, [id]: !prev[id] }));

  const onEdit = (creationItem) => {
    setSelectedCreation(creationItem);
    setShowUpdateModal(true);
  };

  const onCreate = () => {
    setShowCreateModal(true);
  };

  // -----------------------------------------
  // 12. Chart Section - Creation Streak
  // -----------------------------------------
  const { chartData, bucketDates } = React.useMemo(() => {
    // 25 Jan 2026
    const items = userScopedCreations;

    // const items = finalCreations;
    // let items = finalCreations;

    // // ↓↓↓ only thin data in yearly view ↓↓↓
    //   if (activeTimeBucket === "year") {
    //     items = items.filter((c, idx) => idx % 7 === 0); // keep approx weekly points
    //   }

    if (!items || items.length === 0) return { chartData: [], bucketDates: [] };

    let start, end;

    switch (activeTimeBucket) {
      case "week":
        start = startOfWeek;
        end = endOfWeek;
        break;
      case "month":
        start = startOfMonth;
        end = endOfMonth;
        break;
      case "quarter":
        start = startOfQuarter;
        end = endOfQuarter;
        break;
      case "year": {
        // Filter only creations from THIS calendar year
        const yearItems = items.filter((c) =>
          moment(c.createdAt).isSame(moment(), "year")
        );

        if (yearItems.length > 0) {
          // Mimic "all" behavior but ONLY within the year
          const sorted = [...yearItems].sort((a, b) =>
            moment(a.createdAt).diff(moment(b.createdAt))
          );

          start = moment(sorted[0].createdAt).startOf("day");
          end = moment(sorted[sorted.length - 1].createdAt).endOf("day");
        } else {
          // No creations this year → fallback to normal yearly range
          start = startOfYear;
          end = endOfYear;
        }
        break;
      }
      // case "year":
      //   start = startOfYear;
      //   end = endOfYear;
      //   break;

      // ⭐ 02 Jan 2026 - NEW — By Year chart domain
      case "byYear": {
        const yearItems = items.filter((c) =>
          moment(c.createdAt).year() === activeYear
        );

        if (yearItems.length > 0) {
          const sorted = [...yearItems].sort((a, b) =>
            moment(a.createdAt).diff(moment(b.createdAt))
          );

          start = moment(sorted[0].createdAt).startOf("day");
          end = moment(sorted[sorted.length - 1].createdAt).endOf("day");
        } else {
          // fallback: full calendar year
          start = moment().year(activeYear).startOf("year");
          end = moment().year(activeYear).endOf("year");
        }
        break;
      }

      case "all": {
        const sorted = [...items].sort((a, b) =>
          moment(a.createdAt).diff(moment(b.createdAt))
        );
        start = moment(sorted[0]?.createdAt).startOf("day");
        end = moment(sorted[sorted.length - 1]?.createdAt).endOf("day");
        break;
      }
      case "today":
      default:
        start = startOfToday;
        end = endOfToday;
        break;
    }

    const bucketDates = [];
    let cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day")) {
      bucketDates.push(cursor.format("YYYY-MM-DD"));
      cursor.add(1, "day");
    }

    const dateToIndexMap = {};
    bucketDates.forEach((d, i) => (dateToIndexMap[d] = i));

    const chartData = items.map((c) => {
      // const d = moment.utc(c.createdAt);

      const d = moment(c.createdAt);

      if (!d.isValid()) return null; // skip invalid dates
      const dateKey = d.format("YYYY-MM-DD");

      // 🚨 NEW FIX: ignore items whose date is outside the current bucket range
      if (!dateToIndexMap.hasOwnProperty(dateKey)) return null;

      const xValue = dateToIndexMap[dateKey] ?? 0;
      const epochMinutes = d.hours() * 60 + d.minutes();

      return {
        creationId: c._id,
        dateKey,
        xValue,
        xLabel: moment(dateKey).format("DD MMM"),
        epochMinutes,
        createdAt: c.createdAt,
      };
    })
    .filter(Boolean)      // remove nulls
    .sort((a, b) => a.xValue - b.xValue);

    // chartData.sort((a, b) => a.xValue - b.xValue);
    return { chartData, bucketDates };
  }, [
    // finalCreations,    // 25 Jan 2026 - commented out
    userScopedCreations,  // 25 Jan 2026 - added
    activeTimeBucket,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
    startOfToday,
    endOfToday,
  ]);

  const allDatePoints = bucketDates.map((d, index) => ({
    xValue: index,
    xLabel: moment(d).format("DD MMM"),
  }));

  // console.log('allDatePoints', allDatePoints)

  // -----------------------------------------
  // NEW: 12B. Daily Count + Cumulative Count Chart Data
  // -----------------------------------------
  const dailyCountData = React.useMemo(() => {
    if (!bucketDates || bucketDates.length === 0) return [];

    // Initialize counts
    const dateCountMap = {};
    bucketDates.forEach((d) => (dateCountMap[d] = 0));

    // Count creations per date
    // finalCreations.forEach((c) => {             // 25 Jan 2026 - commented out
    userScopedCreations.forEach((c) => {           // 25 Jan 2026 - added
      const dateKey = moment(c.createdAt).format("YYYY-MM-DD");
      if (dateCountMap[dateKey] !== undefined) {
        dateCountMap[dateKey] += 1;
      }
    });

    // Build result array with cumulative
    let cumulative = 0;

    return bucketDates.map((d, index) => {
      const count = dateCountMap[d];
      cumulative += count;

      return {
        xValue: index,
        dateKey: d, // ⭐ 02 Jan 2026 - NEW — YYYY-MM-DD
        xLabel: moment(d).format("DD MMM"),
        dailyCount: count,
        cumulativeCount: cumulative,
      };
    });
  }, [
    bucketDates, 
    // finalCreations,    // 25 Jan 2026 - commented out
    userScopedCreations,  // 25 Jan 2026 - added
  ]);


  // --------------------------------------------------
  // 13. Time Scope Explainer (Creations) - 02 Jan 2026
  // --------------------------------------------------
  const timeBucketDescription = React.useMemo(() => {
    switch (activeTimeBucket) {
      case "today":
        return (
          <>
            Today is{" "}
            <span className="font-semibold">
              {now.format("DD MMM YYYY")}
            </span>
          </>
        );

      case "week":
        return (
          <>
            This week — Mon (
            <span className="font-semibold">
              {startOfWeek.format("DD MMM YYYY")}
            </span>
            ) - Sun (
            <span className="font-semibold">
              {actualEndOfWeek.format("DD MMM YYYY")}
            </span>
            )
          </>
        );

      case "month":
        return (
          <>
            This month is{" "}
            <span className="font-semibold">
              {startOfMonth.format("MMMM YYYY")}
            </span>
          </>
        );

      case "quarter": {
        const quarter = now.quarter();
        const endOfQuarterActual = moment()
          .year(now.year())
          .quarter(quarter)
          .endOf("quarter");

        return (
          <>
            This quarter is{" "}
            <span className="font-semibold text-orange-600">
              Q{quarter} of {now.year()}
            </span>
            . Starting:{" "}
            <span className="font-semibold">
              {startOfQuarter.format("DD MMM YYYY")}
            </span>
            , Ending:{" "}
            <span className="font-semibold">
              {endOfQuarterActual.format("DD MMM YYYY")}
            </span>
          </>
        );
      }

      case "year":
        return (
          <>
            This year is{" "}
            <span className="font-semibold">
              {now.year()}
            </span>
          </>
        );

      case "byYear":
        return (
          <>
            Showing data for year{" "}
            <span className="font-semibold">
              {activeYear}
            </span>
          </>
        );

      case "all":
        return (
          <>
            <span className="font-semibold">All</span> the logged creations
          </>
        );

      default:
        return null;
    }
  }, [
    activeTimeBucket,
    activeYear,
    now,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    startOfQuarter,
  ]);


  // -----------------------------------------
  // ⭐ 25 Jan 2026 - Phase 3 — Group Creations by User
  // -----------------------------------------
  const creationsByUser = React.useMemo(() => {
    const map = new Map();

    userScopedCreations.forEach(c => {
      const uid = c.userId?._id;
      if (!uid) return;

      if (!map.has(uid)) {
        map.set(uid, {
          userId: uid,
          userName: c.userId?.name ||  "User",
          username: c.userId?.username || "",
          creations: [],
        });
      }

      map.get(uid).creations.push(c);
    });

    return Array.from(map.values());
  }, [userScopedCreations]);

  
  // -----------------------------------------
  // ⭐ Phase 3 — Per User Streak Data (Compare)
  // -----------------------------------------
  const streakDataByUser = React.useMemo(() => {
    if (!bucketDates || bucketDates.length === 0) return [];

    // Build date → xValue map once
    const dateToIndexMap = {};
    bucketDates.forEach((d, i) => {
      dateToIndexMap[d] = i;
    });

    return creationsByUser.map(userBlock => {
      const points = userBlock.creations
        .map(c => {
          // const d = moment.utc(c.createdAt); 
          const d = moment(c.createdAt);
          if (!d.isValid()) return null;

          const dateKey = d.format("YYYY-MM-DD");
          if (!dateToIndexMap.hasOwnProperty(dateKey)) return null;

          return {
            xValue: dateToIndexMap[dateKey],
            dateKey,
            epochMinutes: d.hours() * 60 + d.minutes(),
            createdAt: c.createdAt,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.xValue - b.xValue);

      return {
        userId: userBlock.userId,
        userName: userBlock.userName,
        data: points,
      };
    });
  }, [creationsByUser, bucketDates]);

  
  
  // -----------------------------------------
  // ⭐ Phase 3 — Per User Daily + Cumulative Data (Compare)
  // -----------------------------------------
  const dailyCountByUser = React.useMemo(() => {
    if (!bucketDates || bucketDates.length === 0) return [];

    return creationsByUser.map(userBlock => {
      const dateCountMap = {};
      bucketDates.forEach(d => (dateCountMap[d] = 0));

      // Count per day for this user
      userBlock.creations.forEach(c => {
        const dateKey = moment(c.createdAt).format("YYYY-MM-DD");
        if (dateCountMap[dateKey] !== undefined) {
          dateCountMap[dateKey] += 1;
        }
      });

      let cumulative = 0;

      const series = bucketDates.map((d, index) => {
        const count = dateCountMap[d];
        cumulative += count;

        return {
          xValue: index,
          dateKey: d,
          xLabel: moment(d).format("DD MMM"),
          dailyCount: count,
          cumulativeCount: cumulative,
        };
      });

      return {
        userId: userBlock.userId,
        userName: userBlock.userName,
        data: series,
      };
    });
  }, [creationsByUser, bucketDates]);

 // ⭐ 25 Jan 2026 - Permission check for Creation edit/delete
 const canEditCreation = (creation, loggedInUserId) => {
   // Always allow own creations
   if (creation.userId?._id === loggedInUserId) return true;

   // Allow if explicitly shared
   if (Array.isArray(creation.sharedWith)) {
     return creation.sharedWith.some(
       sw => sw.userId === loggedInUserId || sw.userId?._id === loggedInUserId
     );
   }

   return false;
 };

 // ⭐ 25 Jan 2026 - Time-scoped creations (used by charts + contributions)
 const timeScopedCreations = React.useMemo(() => {
    switch (activeTimeBucket) {
      case "week":
        return creationsThisWeek;
      case "month":
        return creationsThisMonth;
      case "quarter":
        return creationsThisQuarter;
      case "year":
        return creationsThisYear;
      case "byYear":
        return userScopedCreations.filter(c =>
          moment(c.createdAt).year() === activeYear
        );
      case "all":
        return userScopedCreations;
      case "today":
      default:
        return creationsToday;
    }
  }, [
    activeTimeBucket,
    creationsToday,
    creationsThisWeek,
    creationsThisMonth,
    creationsThisQuarter,
    creationsThisYear,
    userScopedCreations,
    activeYear,
 ]);

 // ⭐ 25 Jan 2026 - Group time-scoped creations by user
 const timeScopedCreationsByUser = React.useMemo(() => {
  const map = new Map();

  timeScopedCreations.forEach(c => {
    const uid = c.userId?._id;
    if (!uid) return;

    if (!map.has(uid)) {
      map.set(uid, {
        userId: uid,
        userName: c.userId?.name ||  "User",
        username: c.userId?.username || "",
        creations: [],
      });
    }

    map.get(uid).creations.push(c);
  });

  return Array.from(map.values());
 }, [timeScopedCreations]);



 // -----------------------------------------
 // ⭐ 25 Jan 2026 - Collective Creation Contribution Snapshot
 // -----------------------------------------

 const creationContributionSnapshot = React.useMemo(() => {
  if (!timeScopedCreationsByUser.length) return null;

  const total = timeScopedCreationsByUser.reduce(
    (sum, u) => sum + u.creations.length,
    0
  );

  if (total === 0) return null;

  return {
    total,
    contributors: timeScopedCreationsByUser.map(u => ({
      userId: u.userId,
      userName: u.userName,
      username: u.username,
      count: u.creations.length,
      percentOfTotal: Math.round((u.creations.length / total) * 100),
    }))
    .sort((a, b) => b.percentOfTotal - a.percentOfTotal),
  };
}, [timeScopedCreationsByUser]);

// 26 Jan 2026 - Handle Collective User Select - Switch from Everyone to other users for "Contributions" Tab
const handleCollectiveUserSelect = (userId) => {
  setActiveCollectiveUserId(userId);

  if (userId !== "all" && activeViewTab === "contributions") {
    setActiveViewTab("log");
  }
};

// -----------PURPOSE NATIVE UNIVERSE BLOCK -------------------

// ⭐ 26 Jan 2026 - For filterig for Universe views
  const isUniverseView = viewMode === "universe";

  // ⭐ 26 Jan 2026 — Universe: normalize org daily totals by date
  const universeDateCountMap = React.useMemo(() => {
    const map = {};

    orgDailyTotalsRaw.forEach(row => {
      if (!row?.date || typeof row.dailyCount !== "number") return;

      // date already comes as YYYY-MM-DD from backend
      map[row.date] = (map[row.date] || 0) + row.dailyCount;
    });

    return map;
  }, [orgDailyTotalsRaw]);

  // ⭐ 26 Jan 2026 — Universe bucket dates (time scoped)
  const universeBucketDates = React.useMemo(() => {
    if (!isUniverseView) return [];

    let start, end;

    switch (activeTimeBucket) {
      case "week":
        start = startOfWeek;
        end = endOfWeek;
        break;
      case "month":
        start = startOfMonth;
        end = endOfMonth;
        break;
      case "quarter":
        start = startOfQuarter;
        end = endOfQuarter;
        break;
      case "year":
        start = startOfYear;
        end = endOfYear;
        break;
      case "byYear": {
        start = moment().year(activeYear).startOf("year");
        end = moment().year(activeYear).isSame(moment(), "year")
          ? endOfToday
          : moment().year(activeYear).endOf("year");
        break;
      }
      case "all": {
        const dates = Object.keys(universeDateCountMap);
        if (!dates.length) return [];

        const sorted = dates.sort();
        start = moment(sorted[0]);
        end = moment(sorted[sorted.length - 1]);
        break;
      }
      case "today":
      default:
        start = startOfToday;
        end = endOfToday;
        break;
    }

    const buckets = [];
    let cursor = start.clone();

    while (cursor.isSameOrBefore(end, "day")) {
      buckets.push(cursor.format("YYYY-MM-DD"));
      cursor.add(1, "day");
    }

    return buckets;
  }, [
    isUniverseView,
    activeTimeBucket,
    universeDateCountMap,
    startOfToday,
    endOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
    activeYear,
  ]);

  // ⭐ 26 Jan 2026 — Universe totals for active time bucket
  const universeTimeScopedTotals = React.useMemo(() => {
    if (!orgDailyTotalsRaw.length) return [];

    return orgDailyTotalsRaw.filter(d => {
      if (!d.date) return false;
      const m = moment(d.date);

      switch (activeTimeBucket) {
        case "today":
          return m.isSame(startOfToday, "day");

        case "week":
          return m.isBetween(startOfWeek, endOfWeek, null, "[]");

        case "month":
          return m.isBetween(startOfMonth, endOfMonth, null, "[]");

        case "quarter":
          return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");

        case "year":
          return m.isBetween(startOfYear, endOfYear, null, "[]");

        case "byYear":
          return m.year() === activeYear;

        case "all":
        default:
          return true;
      }
    });
  }, [
    orgDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐ 26 Jan 2026 - Universe aggregate count (no users involved)
  const universeTotalCount = React.useMemo(() => {
    return universeTimeScopedTotals.reduce(
      (sum, d) => sum + (d.dailyCount || 0),
      0
    );
  }, [universeTimeScopedTotals]);

  // ⭐ 26 Jan 2026 — Universe daily + cumulative series (chart-ready)
  const universeDailyCumulativeData = React.useMemo(() => {
    if (!isUniverseView || !universeBucketDates.length) return [];

    let cumulative = 0;

    return universeBucketDates.map((dateKey, index) => {
      const dailyCount = universeDateCountMap[dateKey] || 0;
      cumulative += dailyCount;

      return {
        xValue: index,
        dateKey,
        xLabel: moment(dateKey).format("DD MMM"),
        dailyCount,
        cumulativeCount: cumulative,
      };
    });
  }, [
    isUniverseView,
    universeBucketDates,
    universeDateCountMap,
  ]);

  // ⭐ 26 Jan 2026 — My contribution within Universe scope
  const myUniverseContributionCount = React.useMemo(() => {
    if (!allCreationsRaw.length) return 0;

    return allCreationsRaw.filter(r => {
      if (!r.createdAt) return false;
      if (r.userOrgId?._id !== userOrgId) return false;


      const m = moment(r.createdAt);

      switch (activeTimeBucket) {
        case "today":
          return m.isSame(startOfToday, "day");

        case "week":
          return m.isBetween(startOfWeek, endOfWeek, null, "[]");

        case "month":
          return m.isBetween(startOfMonth, endOfMonth, null, "[]");

        case "quarter":
          return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");

        case "year":
          return m.isBetween(startOfYear, endOfYear, null, "[]");

        case "byYear":
          return m.year() === activeYear;

        case "all":
        default:
          return true;
      }
    }).length;
  }, [
    allCreationsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐ 26 Jan 2026 — My share of universe (%)
  const myUniverseSharePct = React.useMemo(() => {
    if (!universeTotalCount) return 0;
    return Math.round((myUniverseContributionCount / universeTotalCount) * 100);
  }, [myUniverseContributionCount, universeTotalCount]);

  // ⭐ 26 Jan 2026 — Universe daily intensity
  const universeDailyAverage = React.useMemo(() => {
    if (!universeBucketDates.length) return 0;
    return Math.round(universeTotalCount / universeBucketDates.length);
  }, [universeTotalCount, universeBucketDates]);

  // ⭐ 26 Jan 2026 — Universe totals per org (for ranking)
  const universeTotalsByOrg = React.useMemo(() => {
  const map = {};

    universeTimeScopedTotals.forEach(row => {
      if (!row?.orgId || typeof row.dailyCount !== "number") return;

      // const orgId = row.orgId.toString();

      const orgId =
      row.orgId?._id?.toString?.() ??
      row.orgId?.toString?.() ??
      null;

      if (!orgId) return;

      map[orgId] = (map[orgId] || 0) + row.dailyCount;
    });

    return map;
  }, [universeTimeScopedTotals]);

  // ⭐ 26 Jan 2026 — Universe org totals (sorted)
  const universeOrgTotalsSorted = React.useMemo(() => {
    return Object.entries(universeTotalsByOrg)
      .map(([orgId, total]) => ({ orgId, total }))
      .sort((a, b) => b.total - a.total);
  }, [universeTotalsByOrg]);

  // ⭐ 26 Jan 2026 — My org rank in universe
  const universeRankMeta = React.useMemo(() => {
    if (!userOrgId || !universeOrgTotalsSorted.length) return null;

    const myOrgId = userOrgId.toString();

    const index = universeOrgTotalsSorted.findIndex(
      o => o.orgId === myOrgId
    );

    if (index === -1) {
      return {
        myRank: null,
        totalOrgs: universeOrgTotalsSorted.length,
        myOrgTotal: 0,
      };
    }

    return {
      myRank: index + 1,
      totalOrgs: universeOrgTotalsSorted.length,
      myOrgTotal: universeOrgTotalsSorted[index].total,
    };
  }, [universeOrgTotalsSorted, userOrgId]);

  // ⭐ 26 Jan 2026 — Universe benchmarks
  const universeBenchmarks = React.useMemo(() => {
    if (!universeOrgTotalsSorted.length) return null;

    const totals = universeOrgTotalsSorted.map(o => o.total);
    const max = totals[0];
    const min = totals[totals.length - 1];
    const median =
      totals.length % 2 === 1
        ? totals[Math.floor(totals.length / 2)]
        : Math.round(
            (totals[totals.length / 2 - 1] + totals[totals.length / 2]) / 2
          );

    return { max, min, median };
  }, [universeOrgTotalsSorted]);

  // ⭐ 26 Jan 2026 — Human-friendly Universe time label
  const universeTimeLabel = React.useMemo(() => {
    switch (activeTimeBucket) {
      case "today":
        return "today";
      case "week":
        return "this week";
      case "month":
        return "this month";
      case "quarter":
        return "this quarter";
      case "year":
        return "this year";
      case "byYear":
        return `year ${activeYear}`;
      case "all":
        return "all time periods";
      default:
        return activeTimeBucket;
    }
  }, [activeTimeBucket, activeYear]);

  // ⭐⭐ 29 Jan 2026 - orgUserDailyTotalsRaw
  const orgUserDailyTotalsRaw = React.useMemo(() => {
    if (!creationsResponse || Array.isArray(creationsResponse)) return [];
    return creationsResponse.orgUserDailyTotals || [];
  }, [creationsResponse]);

  // ⭐⭐ 30 Jan 2026
  const universeByOrgMap = React.useMemo(() => {
    const map = {};

    const isInActiveTimeBucket = (dateStr) => {
      const m = moment(dateStr);

      switch (activeTimeBucket) {
        case "today":
          return m.isSame(startOfToday, "day");

        case "week":
          return m.isBetween(startOfWeek, endOfWeek, null, "[]");

        case "month":
          return m.isBetween(startOfMonth, endOfMonth, null, "[]");

        case "quarter":
          return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");

        case "year":
          return m.isBetween(startOfYear, endOfYear, null, "[]");

        case "byYear":
          return m.year() === activeYear;

        case "all":
        default:
          return true;
      }
    };

    orgUserDailyTotalsRaw
      .filter(row => row?.date && isInActiveTimeBucket(row.date))
      .forEach(row => {
        const orgId = row.orgId?.toString();
        const userId = row.userId?.toString();

        if (!orgId || !userId) return;

        if (!map[orgId]) {
          map[orgId] = {
            total: 0,
            users: {},
          };
        }

        map[orgId].total += row.dailyCount;
        map[orgId].users[userId] =
          (map[orgId].users[userId] || 0) + row.dailyCount;
      });

    return map;
  }, [
    orgUserDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // console.log('universeByOrgMap', universeByOrgMap)

  // ⭐⭐ 30 Jan 2026   
  const userIdToFirstNameMap = React.useMemo(() => {
    const map = {};

    allUsers.forEach(user => {
      if (!user?._id) return;

      const displayName =
        user.name ||
        user.username ||
        "User";

      map[user._id.toString()] = displayName.split(" ")[0];
    });

    return map;
  }, [allUsers]);

  // ⭐ 31 Jan 2026 
  const universeOrgCumulativeSeries = React.useMemo(() => {
    const map = {};

    orgDailyTotalsRaw
      .filter(row => {
        if (!row?.date) return false;
        const m = moment(row.date);

        switch (activeTimeBucket) {
          case "today":
            return m.isSame(startOfToday, "day");
          case "week":
            return m.isBetween(startOfWeek, endOfWeek, null, "[]");
          case "month":
            return m.isBetween(startOfMonth, endOfMonth, null, "[]");
          case "quarter":
            return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");
          case "year":
            return m.isBetween(startOfYear, endOfYear, null, "[]");
          case "byYear":
            return m.year() === activeYear;
          case "all":
          default:
            return true;
        }
      })
      .forEach(row => {
        const orgId = row.orgId?.toString();
        if (!orgId) return;

        if (!map[orgId]) {
          map[orgId] = {};
        }

        map[orgId][row.date] =
          (map[orgId][row.date] || 0) + row.dailyCount;
      });

    // convert to cumulative series
    const result = {};

    Object.entries(map).forEach(([orgId, dateMap]) => {
      let cumulative = 0;

      const series = Object.keys(dateMap)
        .sort()
        .map((date, idx) => {
          cumulative += dateMap[date];
          return {
            xValue: idx,
            dateKey: date,
            xLabel: moment(date).format("DD MMM"),
            cumulativeCount: cumulative,
          };
        });

      result[orgId] = series;
    });

    return result;
  }, [
    orgDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐ 31 Jan 2026 - rankBgClass - color the org ranks
  const rankBgClass = (idx) =>
  idx === 0
    ? "bg-lime-600 text-white"
    : idx === 1
    ? "bg-yellow-400 text-gray-900"
    : idx === 2
    ? "bg-orange-200 text-gray-900"
    : idx === 3
    ? "bg-purple-100 text-gray-900"
    : idx === 4
    ? "bg-blue-100 text-gray-900"
    : "bg-gray-100 text-gray-700";


  // ⭐ 31 Jan 2026 - get selected users for an org for charting
  const getSelectedUserForOrg = (orgId, users) =>
    selectedOrgUserMap[orgId] || users?.[0]?.[0];

  const handleUserSelect = (orgId, userId) => {
    setSelectedOrgUserMap(prev => ({
      ...prev,
      [orgId]: userId,
    }));
  };

  // ⭐ 31 Jan 2026 – Org → User cumulative reflection series
  const universeUserCumulativeSeries = React.useMemo(() => {
    const map = {};

    orgUserDailyTotalsRaw
      .filter(row => {
        if (!row?.date) return false;
        const m = moment(row.date);

        switch (activeTimeBucket) {
          case "today":
            return m.isSame(startOfToday, "day");
          case "week":
            return m.isBetween(startOfWeek, endOfWeek, null, "[]");
          case "month":
            return m.isBetween(startOfMonth, endOfMonth, null, "[]");
          case "quarter":
            return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");
          case "year":
            return m.isBetween(startOfYear, endOfYear, null, "[]");
          case "byYear":
            return m.year() === activeYear;
          case "all":
          default:
            return true;
        }
      })
      .forEach(row => {
        const orgId = row.orgId?.toString();
        const userId = row.userId?.toString();
        if (!orgId || !userId) return;

        if (!map[orgId]) map[orgId] = {};
        if (!map[orgId][userId]) map[orgId][userId] = {};

        map[orgId][userId][row.date] =
          (map[orgId][userId][row.date] || 0) + row.dailyCount;
      });

    const result = {};

    Object.entries(map).forEach(([orgId, userMap]) => {
      result[orgId] = {};

      Object.entries(userMap).forEach(([userId, dateMap]) => {
        let cumulative = 0;

        result[orgId][userId] = Object.keys(dateMap)
          .sort()
          .map((date, idx) => {
            cumulative += dateMap[date];
            return {
              xValue: idx,
              dateKey: date,
              xLabel: moment(date).format("DD MMM"),
              cumulativeCount: cumulative,
            };
          });
      });
    });

    return result;
  }, [
    orgUserDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // 🔐 05 Feb 2026 — Cryptic Org Name helper
  const getCrypticOrgName = (name = "") => {
    if (!name) return "ORG";

    const parts = name.split(" ").filter(Boolean);
    if (parts.length > 1) {
      return parts.map(p => p[0]).join("").toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // 🔐 05 Feb 2026 — Cryptic User Name helper
  const getCrypticUserName = (fullName = "") => {
    if (!fullName) return "U";

    const parts = fullName.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  // 05 Feb 2026
  const toggleOrgNameVisibility = (orgId) => {
    setShowOrgNameMap(prev => ({
      ...prev,
      [orgId]: !prev[orgId],
    }));
  };

    // ⭐ 21 Apr 2026 - My Org universe snapshot
  const myOrgUniverseData = React.useMemo(() => {
    return universeByOrgMap[userOrgId?.toString()] || { total: 0, users: {} };
  }, [universeByOrgMap, userOrgId]);

  // ⭐ 21 Apr 2026 - ranked users for My Org block
  const myOrgRankedUsers = React.useMemo(() => {
    return Object.entries(myOrgUniverseData?.users || {}).sort((a, b) => b[1] - a[1]);
  }, [myOrgUniverseData]);

  // ⭐ 21 Apr 2026 - My Org cumulative trend
  const myOrgUniverseSeries = React.useMemo(() => {
    return universeOrgCumulativeSeries[userOrgId?.toString()] || [];
  }, [universeOrgCumulativeSeries, userOrgId]);

  // ⭐ 21 Apr 2026 - Universe compare lines by org for My Org tab
  const universeOrgCompareSeries = React.useMemo(() => {
    const myOrgId = userOrgId?.toString();

    return universeOrgTotalsSorted.map((org, idx) => {
      const isMyOrg = org.orgId === myOrgId;

      return {
        orgId: org.orgId,
        label: isMyOrg ? "My Org" : `Org ${idx + 1}`,
        total: org.total,
        isMyOrg,
        series: universeOrgCumulativeSeries[org.orgId] || [],
      };
    });
  }, [universeOrgTotalsSorted, universeOrgCumulativeSeries, userOrgId]);

  // ⭐ 21 Apr 2026 - selected contributor inside My Org block
  const selectedMyOrgUserId = React.useMemo(() => {
    return getSelectedUserForOrg(userOrgId?.toString(), myOrgRankedUsers);
  }, [userOrgId, myOrgRankedUsers, selectedOrgUserMap]);


  // -----------------------------------------
  // 15. UI — Start
  // -----------------------------------------
  
  
  return (
    <div className="p-6 bg-white shadow rounded font-poppins max-w-6xl mx-auto mt-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 animate-slide-in-left">
        <div>
          <div className="flex justify-start items-center">
            <EtWabCreationIcon1SVGComp className="h-6 w-6 md:h-8 md:w-8 text-orange-600" />
            <p className="text-xl font-bold text-gray-800 mx-1 px-1">
              Creation Center
            </p>
          </div>

          <p className="text-sm text-gray-500">
            See how you have been building your{" "}
            <span className="font-bold text-orange-800">creative</span> imprint...
          </p>
        </div>

        <button
          onClick={onClose}
          className="text-sm text-blue-600 hover:underline"
        >
          Close
        </button>
      </div>

      {/* Count Badge */}
      <div className="flex justify-center items-center mb-4 bg-orange-100 py-4 border-t border-b rounded">
        <span className="text-lg text-gray-700 mx-1 px-1">There are</span>

        <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex justify-center items-center text-lg font-bold">
          {finalCreations?.length || 0}
        </div>

        <span className="text-lg text-gray-700 mx-1 px-1">creations!</span>
      </div>

      {/* 25 Jan 2026 - New Collective View - Time Filters + Self/Collective chip */}
      <div className="flex justify-between items-center w-full">

        {/* Time Filters */}
        <div className="flex space-x-2 my-4">
          {[
            { label: "Today", value: "today" },
            { label: "This Week", value: "week" },
            { label: "This Month", value: "month" },
            { label: "This Qtr", value: "quarter" },
            { label: "This Year", value: "year" },
            { label: "By Year", value: "byYear" },   // ✅ 02 Jan 2026 NEW
            { label: "All", value: "all" },
          ].map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setActiveTimeBucket(value)}
              className={`px-3 py-1 rounded-full text-sm font-semibold border shadow ${
                activeTimeBucket === value
                  ? "bg-orange-600 text-white border-orange-700"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ⭐ 25 Jan 2026 — View Mode Toggle (Self | Collective) */}
        <div className="flex items-center space-x-2 my-4 p-2 bg-gray-50 rounded animate-slide-in-left">
          <button
            onClick={() => setViewMode("self")}
            className={`px-3 py-1 rounded-full text-sm font-semibold border ${
              viewMode === "self"
                ? "bg-orange-600 text-white border-orange-700"
                : "bg-gray-100 text-gray-700 border-gray-300"
            }`}
          >
            My Creations
          </button>

          <button
            onClick={() => setViewMode("collective")}
            className={`px-3 py-1 rounded-full text-sm font-semibold border ${
              viewMode === "collective"
                ? "bg-orange-600 text-white border-orange-700"
                : "bg-gray-100 text-gray-700 border-gray-300"
            }`}
          >
            Collective
          </button>

          {/* 26 Jan 2026 - Universe view */}
          <button
            onClick={() => setViewMode("universe")}
            className={`px-3 py-1 rounded-full text-sm font-semibold border ${
              viewMode === "universe"
                ? "bg-orange-600 text-white border-orange-700"
                : "bg-gray-100 text-gray-700 border-gray-300"
            }`}
          >
            Universe
          </button>

        </div>

      </div>
      
      {/* 02 Jan 2026 - Time Scope Explainers */}
      {timeBucketDescription && (
        <div className="mx-1 mt-1 mb-3 px-3 py-2 bg-orange-100 border-l-4 border-orange-500 rounded">
          <p className="text-sm text-gray-700">
            {timeBucketDescription}
          </p>
          
        </div>
      )}

      {/* ⭐ 02 Jan 2026 - By Year Selector */}
      {activeTimeBucket === "byYear" && (
        <div className="flex space-x-2 mb-4 animate-slide-in-left">
          {availableYears.map((year) => (
            <button
              key={year}
              onClick={() => setActiveYear(year)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border shadow ${
                activeYear === year
                  ? "bg-orange-600 text-white border-orange-800"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      {/* ⭐ 25 Jan 2026 — Collective User Selector */}
      {viewMode === "collective" && (
        <div className="flex items-center space-x-2 overflow-x-auto pb-3 animate-slide-in-left">
          <button
            onClick={() => setActiveCollectiveUserId("all")}
            className={`px-3 py-1 rounded-full text-sm font-semibold border whitespace-nowrap ${
              activeCollectiveUserId === "all"
                ? "bg-orange-600 text-white border-orange-700"
                : "bg-gray-100 text-gray-700 border-gray-300"
            }`}
          >
            Everyone
          </button>

          {collectiveUsers.map((user) => (
            <button
              key={user._id}
              onClick={() => handleCollectiveUserSelect(user._id)} // 26 Jan 2026

              // onClick={() => {

              //   setActiveCollectiveUserId(user._id)
              //   if(activeViewTab === "contributions"){
              //     setActiveViewTab("log")
              //   }
              // }}
              // onClick={() => setActiveCollectiveUserId(user._id)}

              className={`px-3 py-1 rounded-full text-sm font-semibold border whitespace-nowrap ${
                activeCollectiveUserId === user._id
                  ? "bg-orange-600 text-white border-orange-700"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              {user.name.split(" ")[0]}
            </button>
          ))}
        </div>
      )}

      {/* View Tabs */}
      {viewMode !== "universe" && (
        <div className="flex space-x-2 mt-6 p-2 bg-gray-50 rounded">
          <button
            onClick={() => setActiveViewTab("log")}
            className={`text-sm font-semibold px-3 py-1 rounded-t 
              ${
                activeViewTab === "log"
                  ? "bg-orange-200 text-orange-800"
                  : "text-gray-500"
              }`}
          >
            Log View
          </button>

          <button
            onClick={() => setActiveViewTab("chart")}
            className={`text-sm font-semibold px-3 py-1 rounded-t 
              ${
                activeViewTab === "chart"
                  ? "bg-orange-200 text-orange-800"
                  : "text-gray-500"
              }`}
          >
            Chart View
          </button>

          <button
            onClick={() => setActiveViewTab("summary")}
            className={`text-sm font-semibold px-3 py-1 rounded-t 
              ${
                activeViewTab === "summary"
                  ? "bg-orange-200 text-orange-800"
                  : "text-gray-500"
              }`}
          >
            Summary View
          </button>

          {/* ⭐ 25 Jan 2026 — Contributions View */}
          {viewMode === "collective" && activeCollectiveUserId === "all" && (
            <button
              onClick={() => setActiveViewTab("contributions")}
              className={`text-sm font-semibold px-3 py-1 rounded-t 
                ${activeViewTab === "contributions" ? "bg-orange-200 text-orange-800" : "text-gray-500"}`}
            >
              Contributions
            </button>
          )}

        </div>
      )}

      {/* LOG VIEW */}
      {viewMode !== "universe" && activeViewTab === "log" && (
        <>
          {/* Search */}
          <div className="mt-4">
            <input
              type="text"
              placeholder="Search creations..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full border px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Add Button */}
          <div className="flex justify-start items-center my-3">
            <p className="font-bold text-xs mr-2">Add a creation</p>
            <button type="button" onClick={onCreate}>
              <PlusIcon className="h-5 w-5 text-white p-1 rounded bg-lime-600 hover:bg-green-600" />
            </button>
          </div>

          {/* Results */}
          <div className="mt-6 pt-4">
            {isLoading ? (
              <p className="text-gray-500 text-sm">Loading your creations...</p>
            ) : finalCreations.length === 0 ? (
              <p className="text-gray-500 text-sm">No creations found.</p>
            ) : (
              <div className="space-y-4">
                {finalCreations
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt) - new Date(a.createdAt)
                  )
                  .map((creationItem, idx) => {

                    // 25 Jan 2026
                    const allowEdit = canEditCreation(creationItem, userId);

                    const cardMode =
                    viewMode === "collective" && !allowEdit
                    ? "summary"
                    : "full";
                    
                    return(
                    <EtCreationCard
                      key={creationItem._id}
                      creationItem={creationItem}
                      index={idx}
                      showDetail={showDetail}
                      toggleShowDetail={toggleShowDetail}
                      handleEdit={onEdit}
                      createMarkup={createMarkup}
                      showOwner={viewMode === "collective" && activeCollectiveUserId === "all"} // ✅ 25 Jan 2026
                      mode={cardMode} // 25 Jan 2026
                    />
                  )})}
              </div>
            )}
          </div>
        </>
      )}

      {/* CHART VIEW */}
      {viewMode !== "universe" && activeViewTab === "chart" && (
        <div className="my-8 py-6 bg-white rounded shadow-sm border border-gray-200 animate-slide-in-left">
          
          {/* 25 Jan 2026 - Phase 3 - Aggregate vs Compare */}
          {viewMode === "collective" && activeCollectiveUserId === "all" &&  (
            <div className="flex space-x-2 mb-3 px-4">
              <button
                onClick={() => setChartViewMode("aggregate")}
                className={`text-xs font-semibold px-3 py-1 rounded 
                  ${chartViewMode === "aggregate"
                    ? "bg-orange-600 text-white rounded-full"
                    : "bg-gray-200 text-gray-700 rounded-full"}`}
              >
                Everyone
              </button>

              <button
                onClick={() => setChartViewMode("compare")}
                className={`text-xs font-semibold px-3 py-1 rounded 
                  ${chartViewMode === "compare"
                    ? "bg-orange-600 text-white rounded-full"
                    : "bg-gray-200 text-gray-700 rounded-full"}`}
              >
                Compare
              </button>
            </div>
          )}


          {/* --- Chart Sub Tabs --- */}
          <div className="flex space-x-2 mb-4 px-4">
            <button
              onClick={() => setChartTab("streak")}
              className={`text-sm font-semibold px-3 py-1 rounded 
                ${chartTab === "streak" ? "bg-orange-200 text-orange-800" : "text-gray-500"}`}
            >
              Streak View
            </button>

            <button
              onClick={() => setChartTab("counts")}
              className={`text-sm font-semibold px-3 py-1 rounded 
                ${chartTab === "counts" ? "bg-orange-200 text-orange-800" : "text-gray-500"}`}
            >
              Daily & Cumulative
            </button>
          </div>

          {/* --- Creation Streak --- */}
          {chartTab === "streak" && (
            <>
              <p className="text-md font-semibold text-gray-700 mb-3 px-4">
                Creation Streak — Time of Day vs Date
              </p>

              <div className="w-full h-[450px] text-xs">
                <ResponsiveContainer>
                  <LineChart
                    // data={chartData}
                    data={chartViewMode === "aggregate" ? chartData : undefined}
                    margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />

                    {/* X AXIS */}
                    <XAxis
                      type="number"
                      dataKey="xValue"
                      // domain={["dataMin", "dataMax"]}
                      domain={[0, allDatePoints.length - 1]}
                      ticks={allDatePoints.map((d) => d.xValue)}
                      tickFormatter={(x) =>
                        allDatePoints.find((d) => d.xValue === x)?.xLabel || ""
                      }
                    />

                    {/* Y AXIS: Time of Day */}
                    <YAxis
                      type="number"
                      domain={[60, 1440]} // 1 AM → midnight
                      ticks={[60, 180, 360, 540, 720, 900, 1080, 1260]}
                      tickFormatter={(m) =>
                        moment()
                          .local()
                          .startOf("day")
                          .add(m, "minutes")
                          .format("h A")
                      }
                      label={{
                        value: "Time",
                        angle: -90,
                        position: "insideLeft",
                        offset: -3,
                      }}
                    />

                    {/* TOOLTIP */}
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0]?.payload;
                        // const dateStr = moment(p.createdAt).format("ddd, DD MMM");
                        const dateStr = moment(p.createdAt).format("ddd, DD MMM YYYY"); // 02 Jan 2026
                        const timeStr = moment(p.createdAt).format("h:mm A");

                        return (
                          <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                            <p className="font-semibold text-gray-700">
                              {dateStr}
                            </p>
                            <p className="text-sm text-gray-600">{timeStr}</p>
                          </div>
                        );
                      }}
                    />

                    <Legend
                      // verticalAlign="bottom"
                      // height={36}
                      wrapperStyle={{ paddingTop: "20px", paddingBottom: "10px" }}
                    />

                    {chartViewMode === "aggregate" && (
                      <Line
                        type="monotone"
                        dataKey="epochMinutes"
                        stroke="#EA580C"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="Everyone"
                        connectNulls
                      />
                    )}

                    {chartViewMode === "compare" &&
                      streakDataByUser.map((user, idx) => (
                        <Line
                          key={user.userId}
                          type="monotone"
                          data={user.data}
                          dataKey="epochMinutes"
                          stroke={[
                            "#EA580C",
                            "#65a30d",
                            "#2563eb",
                            "#7c3aed",
                            "#dc2626",
                          ][idx % 5]}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          name={user.userName}
                          connectNulls
                        />
                      ))
                    }


                    {/* <Line
                      type="monotone"
                      dataKey="epochMinutes"
                      stroke="#EA580C"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#EA580C" }}
                      name="Creation"
                      connectNulls={true}
                    /> */}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
          

          {/* --- DAILY + CUMULATIVE CHART --- */}
          {chartTab === "counts" && (
            <div className="w-full h-[450px] px-4 text-xs">
              <p className="text-md font-semibold text-gray-700 mb-3">
                Daily Creations & Cumulative Trend
              </p>

              <ResponsiveContainer>
                {chartViewMode === "aggregate" ? (
                <LineChart
                  data={dailyCountData}
                  margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  

                  <XAxis
                    type="number"
                    dataKey="xValue"
                    ticks={dailyCountData.map((d) => d.xValue)}
                    tickFormatter={(x) =>
                      dailyCountData.find((d) => d.xValue === x)?.xLabel || ""
                    }
                  />

                  <YAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "Daily & Cumulative Count",
                      angle: -90,
                      position: "insideLeft",   // correct side
                      dx: -10,                  // horizontal shift left
                      dy: 0,                    // keep vertical centered
                      style: { textAnchor: "middle" },
                    }}
                  />

                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0]?.payload;

                      // 02 Jan 2026
                      const dateStr = moment(p.dateKey).format("ddd, DD MMM YYYY");
                      // const dateStr = moment(p.dateKey).format("DD MMM YYYY");

                      return (
                        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                           {/* <p className="font-semibold text-gray-700">{p.xLabel}</p> */}
                          {/* 02 Jan 2026 */}
                          <p className="font-semibold text-gray-700">{dateStr}</p>
                          <p className="text-sm text-gray-600">
                            Daily: {p.dailyCount}
                          </p>
                          <p className="text-sm text-gray-600">
                            Cumulative: {p.cumulativeCount}
                          </p>
                        </div>
                      );
                    }}
                  />

                  <Legend
                      // verticalAlign="bottom"
                      // height={36}
                      wrapperStyle={{ paddingTop: "20px", paddingBottom: "10px" }}
                    />

                  {/* DAILY COUNT LINE */}
                  {/* <Line
                    type="monotone"
                    dataKey="dailyCount"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#3B82F6" }}
                    name="Daily Creations"
                  /> */}

                  <Bar
                    dataKey="dailyCount"
                    // fill="#3B82F6"
                    // fill="#84cc16"
                    fill="#65a30d"
                    name="Daily Creations"
                    barSize={24}
                  />

                  {/* CUMULATIVE COUNT LINE */}
                  <Line
                    type="monotone"
                    dataKey="cumulativeCount"
                    stroke="#EA580C"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#EA580C" }}
                    name="Cumulative Creations"
                  />
                </LineChart>
                 ) : (
                  <LineChart
                    margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                      type="number"
                      dataKey="xValue"
                      ticks={allDatePoints.map(d => d.xValue)}
                      tickFormatter={(x) =>
                        allDatePoints.find(d => d.xValue === x)?.xLabel || ""
                      }
                    />

                    <YAxis
                      type="number"
                      allowDecimals={false}
                      label={{
                        value: "Cumulative Creations",
                        angle: -90,
                        position: "insideLeft",
                        dx: -10,
                        style: { textAnchor: "middle" },
                      }}
                    />

                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;

                        // label === xValue
                        // const dateKey = allDatePoints.find(d => d.xValue === label)?.xLabel;

                        const dateStr = moment(payload[0].payload.dateKey).format("ddd, DD MMM YYYY");


                        return (
                          <div className="bg-white p-3 border border-gray-300 rounded shadow-lg min-w-[180px]">
                            <p className="font-semibold text-gray-700 mb-2">
                              {/* {moment(dateKey, "DD MMM").format("ddd, DD MMM YYYY")} */}
                              {dateStr}

                            </p>

                            <div className="space-y-1">
                              {payload.map((p, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center space-x-2">
                                    <span
                                      className="inline-block w-2 h-2 rounded-full"
                                      style={{ backgroundColor: p.stroke }}
                                    />
                                    <span className="text-gray-700 font-medium">
                                      {p.name}
                                    </span>
                                  </div>
                                  <span className="text-gray-600 font-semibold mx-1">
                                    {p.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }}
                    />


                    <Legend />

                    {dailyCountByUser.map((user, idx) => (
                      <Line
                        key={user.userId}
                        type="monotone"
                        data={user.data}
                        dataKey="cumulativeCount"
                        name={user.userName}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        stroke={[
                          "#EA580C",
                          "#65a30d",
                          "#2563eb",
                          "#7c3aed",
                          "#dc2626",
                        ][idx % 5]}
                      />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          )}

        </div>
      )}

      {/* SUMMARY VIEW */}
      {viewMode !== "universe" && activeViewTab === "summary" && (
        <EtCreationSummaryTiles
          creations={finalCreations}
          creationTypes={creationTypes}
          creationAreaTypes={creationAreaTypes}
        />
      )}

      {/* 25 Jan 2026 - CONTRIBUTIONS VIEW */}
      {viewMode !== "universe" && activeViewTab === "contributions" && activeCollectiveUserId === "all" &&
        creationContributionSnapshot?.contributors?.length > 0 && (
          <div className="mt-6 animate-slide-in-left">
            <p className="text-lg font-bold m-1 p-1">
              Collective Contribution
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
              {creationContributionSnapshot.contributors.map((c, idx) => {
                const isMe = c.userId === userId;

                // console.log("username: ", c.username)

                return (
                  <div
                    key={`${c.userId}-${idx}`}
                    className="bg-white rounded-lg p-3 shadow-sm border text-center"
                  >
                    <CircularProgressbarWithChildren
                      value={c.percentOfTotal}
                      circleRatio={0.75}
                      styles={{
                        path: {
                          stroke: "#f97316",
                          transform: "rotate(-135deg)",
                          transformOrigin: "center center",
                        },
                        trail: {
                          stroke: "#e5e7eb",
                          transform: "rotate(-135deg)",
                          transformOrigin: "center center",
                        },
                      }}
                    >
                      {/* Avatar */}

                      <div className="flex justify-center">
                        <User
                          username={c.username}
                          defaultUserPic={userProfileIcon2}
                        />
                      </div>


                      {/* <div className="flex justify-center">
                        <div className="h-7 w-7 rounded-full bg-orange-100 flex items-center justify-center text-xs font-bold text-orange-700">
                          {c.userName?.[0]}
                        </div>
                      </div> */}

                      {/* Name */}
                      <p className="text-[10px] font-semibold mt-3">
                        {c.userName?.split(" ")[0]}
                        {isMe && (
                          <span className="ml-1 text-[9px] text-orange-600 font-bold">
                            (Me)
                          </span>
                        )}
                      </p>

                      {/* Percent */}
                      <p className="text-xs font-bold text-gray-700">
                        {c.percentOfTotal}%
                      </p>

                      {/* Absolute count */}
                      <p className="text-[10px] text-gray-500">
                        {c.count} creations
                      </p>
                    </CircularProgressbarWithChildren>
                  </div>
                );
              })}
            </div>
          </div>
      )}


       {/* ⭐ 26 Jan 2026 — UNIVERSE VIEW (Dedicated) */}

        {/* ================================
        🌍 Universe View (Executive Level)
        ================================ */}
        {viewMode === "universe" && (
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">

            {/* 30 Jan 2026 - EtAdmin View Tabs*/}
            {canViewAdminUniverse && (
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setUniverseTab("myOrg")}
                  className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                    universeTab === "myOrg"
                      ? "bg-orange-600 text-white border-orange-700"
                      : "bg-gray-100 text-gray-700 border-gray-300"
                  }`}
                >
                  My Org
                </button>

                <button
                  onClick={() => setUniverseTab("byOrg")}
                  className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                    universeTab === "byOrg"
                      ? "bg-orange-600 text-white border-orange-700"
                      : "bg-gray-100 text-gray-700 border-gray-300"
                  }`}
                >
                  By Org
                </button>
              </div>
            )}

            {/* 30 Jan 2026 - View Mode and myOrg */}
            {/* 21 Apr 2026 - charts for myOrg */}
            {viewMode === "universe" && universeTab === "myOrg" && (
              <>
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400">
                      Trinity Universe · <span className="font-bold text-orange-600">Creations</span>
                    </p>
                    <h2 className="text-lg font-semibold text-gray-800">
                      Your Universe Performance — {universeTimeLabel}
                    </h2>
                  </div>
                </div>

                {/* Primary Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                    <p className="text-xs text-orange-700 uppercase tracking-wide">
                      My Org Contribution
                    </p>

                    <div className="mt-2 flex items-end gap-2">
                      <p className="text-3xl font-bold text-orange-800">
                        {universeRankMeta?.myOrgTotal || 0}
                      </p>
                      <p className="text-sm text-orange-600">
                        creations
                      </p>
                    </div>

                    <p className="mt-1 text-xs text-orange-700">
                      {universeTotalCount > 0
                        ? `${Math.round(
                            (universeRankMeta.myOrgTotal / universeTotalCount) * 100
                          )}% of universe`
                        : "—"}
                    </p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <p className="text-xs text-purple-700 uppercase tracking-wide">
                      Universe Rank
                    </p>

                    <div className="mt-2 flex items-end gap-2">
                      <p className="text-3xl font-bold text-purple-800">
                        #{universeRankMeta?.myRank ?? "—"}
                      </p>
                      <p className="text-sm text-purple-600">
                        of {universeRankMeta?.totalOrgs || 0}
                      </p>
                    </div>

                    <p className="mt-1 text-xs text-purple-700">
                      Ranked by total creations
                    </p>
                  </div>

                  <div className="bg-lime-50 rounded-lg p-4 border border-lime-100">
                    <p className="text-xs text-lime-700 uppercase tracking-wide">
                      Universe Activity
                    </p>

                    <div className="mt-2 flex items-end gap-2">
                      <p className="text-3xl font-bold text-lime-800">
                        {universeTotalCount}
                      </p>
                      <p className="text-sm text-lime-600">
                        creations
                      </p>
                    </div>

                    <p className="mt-1 text-xs text-lime-700">
                      Across all organizations
                    </p>
                  </div>

                </div>

                {/* Narrative Insight */}
                {universeRankMeta && universeTotalCount > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Your organization contributed{" "}
                      <span className="font-bold">
                        {Math.round(
                          (universeRankMeta.myOrgTotal / universeTotalCount) * 100
                        )}%
                      </span>{" "}
                      of all creations in the Trinity universe during this period,
                      placing you{" "}
                      <span className="font-bold">
                        #{universeRankMeta.myRank}
                      </span>{" "}
                      out of{" "}
                      <span className="font-bold">
                        {universeRankMeta.totalOrgs}
                      </span>{" "}
                      organizations.
                    </p>
                  </div>
                )}

                {/* 21 Apr 2026 - Expanded My Org universe chart view */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

                  {/* LEFT SIDE — TRUE UNIVERSE VIEW */}
                  <div className="bg-white rounded-lg border p-4 space-y-5">
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        True Universe View
                      </p>
                      <p className="text-xs text-gray-500">
                        How the full universe is trending and how each org is contributing to that movement.
                      </p>
                    </div>

                    {/* Top Left - Full universe trend */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        Universe Activity Trend
                      </p>

                      <div className="h-64">
                        <ResponsiveContainer>
                          <LineChart
                            data={universeDailyCumulativeData}
                            margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="xValue"
                              tick={{ fontSize: 10 }}
                              tickFormatter={(x) =>
                                universeDailyCumulativeData?.[x]?.xLabel || ""
                              }
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;

                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Daily: {p.dailyCount}</p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Legend />
                            <Bar
                              dataKey="dailyCount"
                              fill="#65a30d"
                              name="Daily Universe"
                              barSize={18}
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulativeCount"
                              stroke="#EA580C"
                              strokeWidth={2}
                              dot={{ r: 2 }}
                              name="Cumulative Universe"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Bottom Left - each org contribution trend */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        Org Contribution Trend
                      </p>

                      <div className="h-72">
                        <ResponsiveContainer>
                          <LineChart margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              type="number"
                              dataKey="xValue"
                              tick={{ fontSize: 10 }}
                              tickFormatter={(x) =>
                                universeOrgCompareSeries?.[0]?.series?.[x]?.xLabel || ""
                              }
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;

                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Legend />
                            {universeOrgCompareSeries.map((orgSeries, idx) => (
                              <Line
                                key={orgSeries.orgId}
                                data={orgSeries.series}
                                type="monotone"
                                dataKey="cumulativeCount"
                                stroke={orgSeries.isMyOrg ? "#EA580C" : ["#65a30d", "#2563eb", "#7c3aed", "#dc2626", "#0f766e"][idx % 5]}
                                strokeWidth={orgSeries.isMyOrg ? 3 : 1.75}
                                dot={false}
                                name={orgSeries.label}
                                connectNulls
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE — MY ORG CONTRIBUTORS */}
                  <div className="bg-white rounded-lg border p-4 space-y-5">
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        My Org Contributors
                      </p>
                      <p className="text-xs text-gray-500">
                        Your organization’s momentum and the ranked contribution trend of your people.
                      </p>
                    </div>

                    {/* Top Right - My Org combined trend */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-gray-700">
                          My Org Trend
                        </p>
                        <p className="text-[10px] text-gray-500">
                          Total: <span className="font-bold">{myOrgUniverseData?.total || 0}</span>
                        </p>
                      </div>

                      <div className="h-64 mt-2">
                        <ResponsiveContainer>
                          <LineChart data={myOrgUniverseSeries}>
                            <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                            <XAxis
                              dataKey="xValue"
                              tick={{ fontSize: 10, fill: "#9ca3af" }}
                              interval="preserveStartEnd"
                              tickFormatter={(x) =>
                                myOrgUniverseSeries?.[x]?.xLabel || ""
                              }
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;

                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulativeCount"
                              stroke="#EA580C"
                              strokeWidth={2}
                              dot={{
                                r: 2,
                                fill: "#EA580C",
                                stroke: "#fff",
                                strokeWidth: 1,
                              }}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Bottom Right - ranked contributors + selected user mini chart */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-3">
                        Contributor Momentum
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                        {/* LEFT — Ranked Users */}
                        <div className="space-y-1 md:col-span-1">
                          {!myOrgUniverseData?.users ||
                          Object.keys(myOrgUniverseData.users).length === 0 ? (
                            <p className="text-xs text-gray-400 p-2">
                              No contributor data available for this period.
                            </p>
                          ) : (
                            Object.entries(myOrgUniverseData.users)
                              .sort((a, b) => b[1] - a[1])
                              .map(([uid, count], uIdx, arr) => {
                                const fullName = userIdToFirstNameMap[uid] || "User";

                                return (
                                  <button
                                    key={uid}
                                    onClick={() => handleUserSelect(userOrgId?.toString(), uid)}
                                    className={`w-full flex justify-between items-center text-xs p-2 border-b rounded
                                      ${
                                        selectedMyOrgUserId === uid
                                          ? "bg-orange-100 text-orange-800 font-semibold"
                                          : "hover:bg-gray-50 text-gray-600"
                                      }`}
                                  >
                                    <span className="flex items-center gap-2">
                                      <span className="text-[10px] text-gray-400">
                                        {uIdx + 1}
                                      </span>

                                      <span>
                                        {fullName}
                                      </span>
                                    </span>

                                    <span className="font-bold">{count}</span>
                                  </button>
                                );
                              })
                          )}
                        </div>

                        {/* RIGHT — Selected User Mini Chart */}
                        <div className="h-32 md:col-span-2">
                          <ResponsiveContainer>
                            <LineChart
                              data={
                                universeUserCumulativeSeries?.[userOrgId?.toString()]?.[selectedMyOrgUserId] || []
                              }
                            >
                              <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                              <XAxis hide />
                              <YAxis hide />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const p = payload[0].payload;

                                  return (
                                    <div className="bg-white p-2 border rounded shadow text-xs">
                                      <p className="font-semibold">
                                        {moment(p.dateKey).format("DD MMM YYYY")}
                                      </p>
                                      <p>Cumulative: {p.cumulativeCount}</p>
                                    </div>
                                  );
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="cumulativeCount"
                                stroke="#EA580C"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                                activeDot={{ r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>

                          <p className="mt-1 text-[10px] text-gray-400 text-center">
                            {(() => {
                              const fullName = userIdToFirstNameMap[selectedMyOrgUserId] || "User";

                              return (
                                <>
                                  {fullName} · creation momentum
                                </>
                              );
                            })()}
                          </p>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* 30 Jan 2026 - View Mode and byOrg  */}
            {viewMode === "universe" && universeTab === "byOrg" && canViewAdminUniverse && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(universeByOrgMap)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([orgId, data], idx) => (
                    <div
                      key={orgId}
                      className="bg-white rounded-lg p-4 shadow-sm border"
                    >
                      {/* <p className="text-xs text-gray-400 uppercase">
                        Organization #{idx + 1}
                      </p> */}
                      {/* <p
                        className={`inline-block text-xs uppercase font-semibold px-3 py-1 rounded-full ${rankBgClass(idx)}`}
                      >
                        Organization # {idx + 1}
                      </p> */}
  
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400 uppercase">
                          Organization
                        </p>
  
                        <span
                          className={`text-xs font-bold px-3 py-0.5 rounded-full ${rankBgClass(idx)}`}
                        >
                          # <span className="text-lg">{idx + 1}</span>
                        </span>
                      </div>

                      {/* 05 Feb 2026 - Org Name Visibility Toggle */}
                      
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-lg font-bold text-gray-800">
                          {showOrgNameMap[orgId]
                            ? orgIdToNameMap[orgId] || "Unknown Organization"
                            : getCrypticOrgName(orgIdToNameMap[orgId])}
                        </p>
  
                        <button
                          onClick={() => toggleOrgNameVisibility(orgId)}
                          className="p-1 rounded hover:bg-gray-100"
                          title={showOrgNameMap[orgId] ? "Hide org name" : "Show org name"}
                        >
                          {showOrgNameMap[orgId] ? (
                            <EyeSlashIcon className="h-4 w-4 text-gray-500" />
                          ) : (
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
  
                      {/* <p className="text-lg font-bold text-gray-800">
                        {/* Org ID: {orgId} }
                        {orgIdToNameMap[orgId] || "Unknown Organization"}
                      </p> */}
  
                      <p className="text-sm text-gray-600 mt-1">
                        Total Creations:{" "}
                        <span className="font-bold">{data.total}</span>
                      </p>
  
                      {/* 📈 31 Jan 2026 - Growth Chart */}
                      <div className="mt-4 h-28">
                        <ResponsiveContainer>
                          <LineChart data={universeOrgCumulativeSeries[orgId] || []}>
                            {/* <CartesianGrid strokeDasharray="3 3" /> */}
                            <CartesianGrid
                              strokeDasharray="3 4"
                                stroke="#d1d5db"
                              // strokeDasharray="2 4"
                              // stroke="#e5e7eb"
                            />
  
                            <XAxis
                              dataKey="xValue"
                              tick={{ fontSize: 9, fill: "#9ca3af" }}
                              interval="preserveStartEnd"
                              tickFormatter={(x) =>
                                universeOrgCumulativeSeries[orgId]?.[x]?.xLabel || ""
                              }
                              // tickFormatter={(x) =>
                              //   universeOrgCumulativeSeries[orgId]?.[x]?.xLabel || ""
                              // }
                              // hide
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;
  
                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulativeCount"
                              stroke="#EA580C"
                              strokeWidth={2}
                              // dot={false}
                              dot={{
                                r: 2,
                                fill: "#EA580C",
                                stroke: "#fff",
                                // stroke: "#84cc16",
                                strokeWidth: 1,
                              }}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
  
                      {/* 👥 Contributors + 📈 User Focus */}
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
  
                        {/* LEFT — Ranked Users */}
                        {/* 05 Feb 2026 - Masked Data */}
                        <div className="space-y-1 md:col-span-1">
                          {Object.entries(data.users)
                            .sort((a, b) => b[1] - a[1])
                            .map(([uid, count], uIdx, arr) => {
                              const fullName = userIdToFirstNameMap[uid] || "User";
                              // const showFull = showUserNameMap[uid];

                              return (
                                <button
                                  key={uid}
                                  onClick={() => handleUserSelect(orgId, uid)}
                                  className={`w-full flex justify-between items-center text-xs p-2 border-b rounded
                                    ${
                                      getSelectedUserForOrg(orgId, arr) === uid
                                        ? "bg-orange-100 text-orange-800 font-semibold"
                                        : "hover:bg-gray-50 text-gray-600"
                                    }`}
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-400">
                                      {uIdx + 1}
                                    </span>

                                    <span>
                                      {/* {showFull ? fullName : getCrypticUserName(fullName)} */}
                                      {showOrgNameMap[orgId]
                                      ? fullName
                                      : getCrypticUserName(fullName)}
                                    </span>
                                  </span>

                                  <span className="font-bold">{count}</span>
                                </button>
                              );
                            })}
                        </div>


                        {/* <div className="space-y-1 md:col-span-1">
                          {Object.entries(data.users)
                            .sort((a, b) => b[1] - a[1])
                            .map(([uid, count], uIdx, arr) => (
                              <button
                                key={uid}
                                onClick={() => handleUserSelect(orgId, uid)}
                                className={`w-full flex justify-between items-center text-xs p-2 border-b rounded
                                  ${
                                    getSelectedUserForOrg(orgId, arr) === uid
                                      ? "bg-orange-100 text-orange-800 font-semibold"
                                      : "hover:bg-gray-50 text-gray-600"
                                  }`}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="text-[10px] text-gray-400">
                                    {uIdx + 1}
                                  </span>
                                  <span>{userIdToFirstNameMap[uid] || "User"}</span>
                                </span>
  
                                <span className="font-bold">{count}</span>
                              </button>
                            ))}
                        </div> */}
  
                        {/* RIGHT — Selected User Mini Chart */}
                        <div className="h-32 md:col-span-2">
                          <ResponsiveContainer>
                            <LineChart
                              data={
                                universeUserCumulativeSeries?.[orgId]?.[
                                  getSelectedUserForOrg(
                                    orgId,
                                    Object.entries(data.users).sort((a, b) => b[1] - a[1])
                                  )
                                ] || []
                              }
                            >
                              <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                              <XAxis hide />
                              <YAxis hide />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const p = payload[0].payload;
  
                                  return (
                                    <div className="bg-white p-2 border rounded shadow text-xs">
                                      <p className="font-semibold">
                                        {moment(p.dateKey).format("DD MMM YYYY")}
                                      </p>
                                      <p>Cumulative: {p.cumulativeCount}</p>
                                    </div>
                                  );
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="cumulativeCount"
                                stroke="#EA580C"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                                activeDot={{ r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>

                          {/* 05 Feb 2026 */}
                          <p className="mt-1 text-[10px] text-gray-400 text-center">
                              {(() => {
                                const selectedUid = getSelectedUserForOrg(
                                  orgId,
                                  Object.entries(data.users).sort((a, b) => b[1] - a[1])
                                );

                                const fullName = userIdToFirstNameMap[selectedUid] || "User";

                                return (
                                  <>
                                    {/* {showUserNameMap[selectedUid]
                                      ? fullName
                                      : getCrypticUserName(fullName)}{" "}
                                    · reflection momentum */}
                                    {showOrgNameMap[orgId]
                                      ? fullName
                                      : getCrypticUserName(fullName)} · creation momentum
                                  </>
                                );
                              })()}
                            </p>
  
                          {/* <p className="mt-1 text-[10px] text-gray-400 text-center">
                            {(userIdToFirstNameMap[
                              getSelectedUserForOrg(
                                orgId,
                                Object.entries(data.users).sort((a, b) => b[1] - a[1])
                              )
                            ] || "User")} · creation momentum
                          </p> */}
                        </div>
  
                      </div>
                      
                    </div>
                  ))}
              </div>
            )}
  
          </div>
        )}



      {/* Modals */}
      {showCreateModal && (
        <EtModalCreateUserImprintCreation
          isOpen={showCreateModal}
          userId={userId}
          userOrgId={userOrgId}
          triggeringUrl={window.location.pathname}
          onClose={() => {
            setShowCreateModal(false);
            refetchUserCreations();
          }}
        />
      )}

      {showUpdateModal && selectedCreation && (
        <EtModalUpdateUserImprintCreation
          isOpen={showUpdateModal}
          id={selectedCreation?._id}
          userId={userId}
          userOrgId={userOrgId}
          triggeringUrl={window.location.pathname}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedCreation(null);
            refetchUserCreations();
          }}
        />
      )}
    </div>
  );
};

export default EtTrinityCreationCenter;